import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';
import ModalHeader from '@components/modals/ModalHeader';
import { toastError, toastSuccess } from '@components/Toast';

import useSingleFileUpload from '@hooks/useSingleFileUpload';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { addSupplierSafetyDocument, getSupplierDocuments } from '@services/supplierOnboardingService';

import { getFileType, getIconByFileType } from '@utils/Util';

import CustomDropdown from '@/components/CustomDropdown';

const auditTypes = [
	{ id: 'CODE_OF_CONDUCT_AUDIT', value: 'Code of conduct Audit' },
	{ id: 'TECHNICAL_AUDIT', value: 'Technical Audit' },
	{ id: 'FINANCIAL_AUDIT', value: 'Financial Audit' }
];

const AddAuditReport = ({ onClose }) => {
	const [inputData, setInputData] = useState({});
	const { readFile } = useSingleFileUpload();
	const [uploadedFile, setUploadedFile] = useState(null);
	const dispatch = useDispatch();
	const { id } = useParams();
	const [errors, setErrors] = useState({});

	const isValid = () => {
		let err = {};
		if (!inputData?.auditType) {
			err['auditTypeError'] = 'Field is required';
		}
		if (!inputData?.expiryDate) {
			err['expiryDateError'] = 'Field is required';
		}
		if (!uploadedFile || uploadedFile.length === 0) {
			err['fileError'] = 'File is required';
		}
		if (inputData?.auditDate && inputData?.expiryDate) {
			const auditDate = new Date(inputData.auditDate);
			const expiryDate = new Date(inputData.expiryDate);
			if (auditDate > expiryDate) {
				err['auditDateError'] = 'Audit date must be less than or equal to expiry date';
			}
		}
		setErrors(err);
		return Object.keys(err).length === 0;
	};

	const handleSubmit = async () => {
		if (isValid()) {
			dispatch(openLoader());
			await addSupplierSafetyDocument(id, {
				documentDTO: uploadedFile[0],
				...inputData,
				auditReportType: inputData.auditType.id
			});
			await dispatch(getSupplierDocuments(id));
			toastSuccess('Updated successfully');
			dispatch(closeLoader());
			onClose();
		}
	};

	const handleItemClick = (item) => {
		setInputData((prev) => ({ ...prev, auditType: item }));
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setInputData((prev) => ({ ...prev, [name]: value }));
	};

	const handleFileChange = async (e) => {
		try {
			const files = e.target.files;
			if (!files) {
				toastError('No files selected!');
				return;
			}
			const readFilePromises = Array.from(files).map((file) =>
				readFile(file).then((base64Str) => {
					let documentType = 'SUPPLIER_AUDIT_REPORT';
					return {
						name: file.name,
						docMimeType: file.type,
						documentType: documentType,
						base64Str
					};
				})
			);

			const data = await Promise.all(readFilePromises);
			setUploadedFile(data);
		} catch {
			toastError('Failed to upload file!');
		}
	};

	const renderTypeWiseDocuments = () => {
		const documentList = uploadedFile || [];
		if (documentList.length > 0) {
			return documentList?.map((item) => {
				let fileType;
				if (item.docUrl) {
					fileType = getFileType(item.docUrl);
				} else {
					fileType = getFileType(item.name);
				}

				return (
					<div>
						<div
							key={`document_${item.id}`}
							className="addition-info d-flex align-items-center cursor-pointer pt-1"
						>
							<img className="file-icon" src={getIconByFileType(fileType)} width={20} alt="" />
							<p className="regular-12 pl-1 gray_dark_02">{item.name}</p>
						</div>
					</div>
				);
			});
		}
	};

	return (
		<ModalContainer className="audit-report-modal">
			<div className="common-popup">
				<ModalHeader title="Audit Report" onClose={onClose} />

				<div className="common-popup-body scroll-y-label">
					<div className="row mb-3">
						<div className="col-md-6">
							<div className="file-uploader small upload-design-image mb-2">
								<label htmlFor="drag-audit-upload" className="drag-upload"></label>
								<input
									type="file"
									className="file-upload"
									name="image"
									id="drag-audit-upload"
									onChange={handleFileChange}
								/>
								{errors?.fileError && <p className="error">{errors.fileError}</p>}
							</div>
						</div>
						<div className="col-md-12 align-items-center d-flex">{renderTypeWiseDocuments()}</div>
					</div>
					<div className="row">
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="audit-type">Audit Type*</label>
								<CustomDropdown
									label="Select Audit Type"
									placeholder="Select Audit Type"
									items={auditTypes}
									onItemClick={handleItemClick}
									selectedItem={inputData?.auditType?.value || ''}
									isShowSearch={false}
								/>
								{errors?.auditTypeError && <p className="error">{errors.auditTypeError}</p>}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="audit-date">Rank</label>
								<input type="text" className="w-100" name="rank" onChange={handleChange} />
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="audit-date">Audit Date</label>
								<input
									type="date"
									className="w-100"
									name="auditDate"
									onChange={handleChange}
									max={inputData?.expiryDate || ''}
								/>
								{errors?.auditDateError && <p className="error">{errors.auditDateError}</p>}
							</div>
						</div>
						<div className="col-md-12">
							<div className="form-group">
								<label htmlFor="expiry-date">Expiry Date*</label>
								<input
									type="date"
									className="w-100"
									name="expiryDate"
									onChange={handleChange}
								/>
								{errors?.expiryDateError && <p className="error">{errors.expiryDateError}</p>}
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32" onClick={onClose}>
							Cancel
						</button>
						<button className="button size32 ml-2" onClick={handleSubmit}>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddAuditReport;
